import React from 'react';
import { useQuery } from '@apollo/client';
import { Card, Col, Row, Spin, Table } from 'antd';
// Api
import { GET_TICKET_EVENT_SALES_REPORT } from 'api/ticketing/queries';
// Types
import {
  GetTicketEventSalesReport,
  GetTicketEventSalesReportVariables,
  GetTicketEventSalesReport_ticketEventSalesReport_byDay,
  GetTicketEventSalesReport_ticketEventSalesReport_byPresaleCode,
  GetTicketEventSalesReport_ticketEventSalesReport_byTier,
} from 'api/ticketing/types/GetTicketEventSalesReport';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
// Helpers
import {
  formatCount,
  formatEventDateTime,
  formatMoney,
  formatVenueAddress,
  pluralize,
  REPEAT_LABELS,
} from 'pages/Events/helpers';
// UI
import { DetailCard, DetailGrid, DetailItem } from 'ui/DetailList/DetailList';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import KpiCard from 'pages/Events/components/KpiCard/KpiCard';
import CheckoutFunnel from 'pages/Events/TicketEventDetails/components/CheckoutFunnel/CheckoutFunnel';
// Styles
import styles from './OverviewTab.module.scss';

type EventDetails = TicketEventDetailsFields;
type TierRow = GetTicketEventSalesReport_ticketEventSalesReport_byTier;
type DayRow = GetTicketEventSalesReport_ticketEventSalesReport_byDay;
type PresaleRow =
  GetTicketEventSalesReport_ticketEventSalesReport_byPresaleCode;

type OverviewTabProps = {
  event: EventDetails;
};

const OverviewTab = ({ event }: OverviewTabProps): JSX.Element => {
  const { data, loading, error, refetch } = useQuery<
    GetTicketEventSalesReport,
    GetTicketEventSalesReportVariables
  >(GET_TICKET_EVENT_SALES_REPORT, {
    variables: { input: { ticketEventId: event.id } },
    fetchPolicy: 'cache-and-network',
  });

  const report = data?.ticketEventSalesReport;
  const totals = report?.totals;
  const at = (value?: string | null) =>
    formatEventDateTime(value, event.timezone);

  return (
    <div className={styles.root}>
      {error && (
        <LoadErrorAlert
          message="Failed to load the sales report"
          error={error}
          onRetry={refetch}
        />
      )}

      {loading && !report && (
        <div className={styles.loading}>
          <Spin />
        </div>
      )}

      {totals && (
        <KpiCard
          colProps={{ xs: 12, md: 8, xl: 4 }}
          items={[
            {
              title: 'Gross revenue',
              value: formatMoney(totals.grossRevenue),
              caption: 'Charged to buyers with fees, before refunds',
            },
            {
              title: 'Host ticket revenue',
              value: formatMoney(totals.ticketRevenue),
              caption: 'Paid tickets less discounts',
            },
            {
              title: 'Tickets sold',
              value: formatCount(totals.ticketsSold),
              suffix: totals.maxAdmissions
                ? `/ ${formatCount(totals.maxAdmissions)}`
                : undefined,
              caption: pluralize(totals.confirmedOrders, 'paid order'),
            },
            {
              title: 'Checked in',
              value: formatCount(totals.checkedIn),
              caption: `${pluralize(totals.compTickets, 'comp ticket')} issued`,
            },
            {
              title: 'Refunded',
              value: formatMoney(totals.refundedRevenue),
              caption: pluralize(totals.refundedOrders, 'order'),
            },
            {
              title: 'In payment review',
              value: formatMoney(totals.inReviewRevenue),
              caption: pluralize(totals.inReviewOrders, 'order'),
            },
          ]}
        />
      )}

      {report && <CheckoutFunnel checkout={report.checkout} />}

      {report && (
        <Card title="Sales by tier" className={styles.card}>
          <Table<TierRow>
            rowKey="tierId"
            size="small"
            pagination={false}
            dataSource={report.byTier}
            scroll={{ x: 720 }}
            locale={{ emptyText: 'This event has no ticket tiers.' }}
            columns={[
              { title: 'Tier', dataIndex: 'tierLabel', key: 'tierLabel' },
              {
                title: 'Price',
                key: 'price',
                render: (_, row) => formatMoney(row.price),
              },
              {
                title: 'Sold',
                key: 'ticketsSold',
                render: (_, row) =>
                  `${formatCount(row.ticketsSold)} / ${formatCount(
                    row.capacity
                  )}`,
              },
              {
                title: 'Comps',
                key: 'compTickets',
                render: (_, row) => formatCount(row.compTickets),
              },
              {
                title: 'Available',
                key: 'available',
                render: (_, row) => formatCount(row.available),
              },
              {
                title: 'Checked in',
                key: 'checkedIn',
                render: (_, row) => formatCount(row.checkedIn),
              },
              {
                title: 'Revenue',
                key: 'revenue',
                align: 'right' as const,
                render: (_, row) => formatMoney(row.revenue),
              },
            ]}
          />
        </Card>
      )}

      {report && (report.byDay.length > 0 || report.byPresaleCode.length > 0) && (
        <Row gutter={16}>
          {report.byDay.length > 0 && (
            <Col xs={24} xl={12}>
              <Card title="Sales by day" className={styles.card}>
                <Table<DayRow>
                  rowKey="day"
                  size="small"
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  dataSource={[...report.byDay].reverse()}
                  columns={[
                    { title: 'Day', dataIndex: 'day', key: 'day' },
                    {
                      title: 'Orders',
                      key: 'orders',
                      render: (_, row) => formatCount(row.orders),
                    },
                    {
                      title: 'Tickets',
                      key: 'ticketsSold',
                      render: (_, row) => formatCount(row.ticketsSold),
                    },
                    {
                      title: 'Revenue',
                      key: 'revenue',
                      align: 'right' as const,
                      render: (_, row) => formatMoney(row.revenue),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}
          {report.byPresaleCode.length > 0 && (
            <Col xs={24} xl={12}>
              <Card title="Presale codes" className={styles.card}>
                <Table<PresaleRow>
                  rowKey="codeId"
                  size="small"
                  pagination={false}
                  dataSource={report.byPresaleCode}
                  columns={[
                    { title: 'Code', dataIndex: 'code', key: 'code' },
                    { title: 'Label', dataIndex: 'label', key: 'label' },
                    {
                      title: 'Tickets',
                      key: 'ticketsSold',
                      render: (_, row) => formatCount(row.ticketsSold),
                    },
                    {
                      title: 'Revenue',
                      key: 'revenue',
                      align: 'right' as const,
                      render: (_, row) => formatMoney(row.revenue),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      <DetailCard title="Schedule">
        <DetailGrid>
          <DetailItem label="Starts" value={at(event.startsAt)} />
          <DetailItem label="Ends" value={at(event.endsAt)} />
          <DetailItem label="Doors open" value={at(event.doorsOpenAt)} />
          <DetailItem
            label="Admission closes"
            value={at(event.admissionClosesAt)}
          />
          <DetailItem label="Sales open" value={at(event.salesOpenAt)} />
          <DetailItem label="Sales close" value={at(event.salesCloseAt)} />
          <DetailItem label="Time zone" value={event.timezone} />
          <DetailItem
            label="Repeats"
            value={event.repeatsEvery ? REPEAT_LABELS[event.repeatsEvery] : ''}
            emptyText="Doesn't repeat"
          />
          {event.originalStartsAt && (
            <DetailItem
              label="Originally scheduled"
              value={at(event.originalStartsAt)}
              hint={
                event.rescheduledAt
                  ? `Rescheduled ${at(event.rescheduledAt)}`
                  : undefined
              }
            />
          )}
          <DetailItem label="Published" value={at(event.publishedAt)} />
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Venue">
        <DetailGrid>
          <DetailItem label="Name" value={event.venueName} />
          <DetailItem
            label="Address"
            value={
              event.venueAddress ? formatVenueAddress(event.venueAddress) : ''
            }
            wide
          />
          <DetailItem
            label="Map"
            value={
              event.latitude !== null && event.longitude !== null ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Google Maps
                </a>
              ) : (
                ''
              )
            }
          />
          <DetailItem
            label="Capacity"
            value={event.maxAdmissions ? formatCount(event.maxAdmissions) : ''}
            emptyText={event.ticketsEnabled ? 'Not set' : 'No tickets'}
          />
        </DetailGrid>
      </DetailCard>

      <DetailCard title="Event info">
        <DetailGrid>
          <DetailItem label="Promotion" value={event.promotion} />
          <DetailItem
            label="Minimum age"
            value={event.minimumAge ? `${event.minimumAge}+` : ''}
            emptyText="All ages"
          />
          <DetailItem
            label="Sports"
            value={event.sports.map(({ name }) => name).join(', ')}
          />
          <DetailItem label="League" value={event.league?.name} />
          <DetailItem
            label="Tags"
            value={event.hashtags.map(({ name }) => `#${name}`).join(' ')}
          />
          <DetailItem
            label="Affiliate program"
            value={
              event.affiliateEnabled
                ? `On, ${event.affiliateCommissionPercent}% commission`
                : 'Off'
            }
          />
          <DetailItem
            label="Fan polling"
            value={event.fanPollingEnabled ? 'On' : 'Off'}
          />
          <DetailItem
            label="Matchcard"
            value={
              event.matchups.length
                ? pluralize(event.matchups.length, 'bout')
                : ''
            }
            emptyText="None"
          />
          <DetailItem label="Description" value={event.description} wide />
          <DetailItem
            label="Entry requirements"
            value={event.entryRequirements}
            wide
          />
          <DetailItem label="Refund policy" value={event.refundPolicy} wide />
          <DetailItem
            label="Terms and conditions"
            value={event.termsAndConditions}
            wide
          />
        </DetailGrid>
      </DetailCard>

      <DetailCard title="References">
        <DetailGrid>
          <DetailItem label="Event ID" value={event.id} mono />
          <DetailItem label="Slug" value={event.slug} mono />
          <DetailItem label="Host user ID" value={event.hostUserId} mono />
          <DetailItem label="Linked stream" value={event.stream?.name} />
          <DetailItem label="Version" value={event.version} mono />
        </DetailGrid>
      </DetailCard>
    </div>
  );
};

export default OverviewTab;
